package org.moxultimate.topwithmox.productcrud.update

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.buttons.PressableButtonStyle
import org.moxultimate.topwithmox.components.crud.CRUDDropdown
import org.moxultimate.topwithmox.components.crud.CRUDExistingDropZone
import org.moxultimate.topwithmox.components.crud.CRUDFormField
import org.moxultimate.topwithmox.components.crud.CRUDStatusBanner
import org.moxultimate.topwithmox.components.crud.CRUDSuccessDialog
import org.moxultimate.topwithmox.components.crud.crudProductContentTypes
import org.moxultimate.topwithmox.components.loading.LoadingIndicator
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.utils.autoTagSync
import org.moxultimate.topwithmox.product.data.ProductContentResponse
import org.moxultimate.topwithmox.product.data.ProductResponse
import org.moxultimate.topwithmox.productcrud.add.ProductFormFields
import org.moxultimate.topwithmox.productcrud.main.ProductCRUDState
import org.moxultimate.topwithmox.productcrud.main.ProductCRUDViewModel
import org.moxultimate.topwithmox.productcrud.main.ProductContentFormEntry
import org.moxultimate.topwithmox.productcrud.main.ProductFormEntry
import org.moxultimate.topwithmox.productcrud.main.toFormEntry
import org.moxultimate.topwithmox.theme.DarkTextColor
import org.moxultimate.topwithmox.theme.FontFamily

/**
 * ProductUpdatePage
 * Edit page for a single existing product content entry.
 * Pre-populates all fields from [content] and calls PUT /product-contents/{id} on save.
 * Below the content form, lists all child products with links to edit/add them.
 *
 * @param content The product content to edit, reconstructed from nav params by the route initializer.
 */
@Composable
fun ProductUpdatePage(content: ProductContentResponse) {
    val viewModel: ProductCRUDViewModel = koinViewModel()
    val crudState by viewModel.crudState.collectAsState()
    val contentProducts by viewModel.contentProducts.collectAsState()
    val isLoadingProducts by viewModel.isLoadingProducts.collectAsState()
    val navigator = rememberAppNavigator()

    LaunchedEffect(content.id) {
        viewModel.loadContentById(content.id)
    }

    // Pre-populate form from the content passed via nav params
    var entry by remember {
        mutableStateOf(
            ProductContentFormEntry(
                title = content.title,
                description = content.description.orEmpty(),
                videoLink = content.videoLink.orEmpty(),
                tags = content.tags.orEmpty(),
                type = content.type.orEmpty(),
                thumbnailPath = content.thumbnailPath.orEmpty(),
            )
        )
    }

    // Show success dialog and navigate back on success
    if (crudState is ProductCRUDState.Success) {
        CRUDSuccessDialog(
            message = (crudState as ProductCRUDState.Success).message,
            onOkay = {
                viewModel.resetState()
                navigator.goToProductCRUD()
            },
        )
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .fillMaxHeight()
            .backgroundColor(Colors.White)
            .overflow(Overflow.Auto),
        contentAlignment = Alignment.TopCenter,
    ) {
        Column(
            modifier = Modifier
                .width(clamp(min = 700.px, value = 70.vw, max = 1000.px))
                .padding(top = 80.px, bottom = 48.px, leftRight = 2.cssRem),
            verticalArrangement = Arrangement.spacedBy(28.px),
        ) {
            // Header row: title + subtitle + back button
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(4.px)) {
                    SpanText(
                        text = "Edit Product Content",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(32.px)
                            .fontWeight(FontWeight.Bold)
                            .moxTextGradient(),
                    )
                    SpanText(
                        text = "ID: ${content.id}  ·  ${contentProducts.size} products",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(13.px)
                            .color(Colors.Gray),
                    )
                }
                SpanText(
                    text = "← Back",
                    modifier = PressableButtonStyle.toModifier()
                        .fontFamily(FontFamily)
                        .fontSize(14.px)
                        .color(Colors.Gray)
                        .onClick { navigator.goBack() },
                )
            }

            // Status banner for content operations
            if (crudState is ProductCRUDState.Error) {
                CRUDStatusBanner(
                    message = (crudState as ProductCRUDState.Error).message,
                    isError = true,
                    onDismiss = { viewModel.resetState() },
                )
            }

            // Content form fields
            CRUDFormField(
                label = "Title *",
                value = entry.title,
                placeholder = "Section title",
                onValueChange = { entry = entry.copy(title = it, tags = autoTagSync(entry.tags, entry.title, it)) },
            )

            CRUDDropdown(
                label = "Type",
                options = crudProductContentTypes,
                selected = entry.type,
                placeholder = "Select type…",
                onSelected = { entry = entry.copy(type = it, tags = autoTagSync(entry.tags, entry.type, it)) },
            )

            CRUDExistingDropZone(
                label = "Thumbnail",
                existingUrl = entry.thumbnailPath,
                file = entry.thumbnailFile,
                onFileDropped = { entry = entry.copy(thumbnailFile = it) },
                onFileRemoved = { entry = entry.copy(thumbnailFile = null) },
            )

            CRUDFormField(
                label = "Tags",
                value = entry.tags,
                placeholder = "e.g. Gaming | Mouse | Budget (pipe-separated)",
                onValueChange = { entry = entry.copy(tags = it) },
            )

            CRUDFormField(
                label = "Video Link",
                value = entry.videoLink,
                placeholder = "YouTube URL (optional)",
                onValueChange = { entry = entry.copy(videoLink = it) },
            )

            CRUDFormField(
                label = "Description",
                value = entry.description,
                placeholder = "Short paragraph shown below the section title",
                onValueChange = { entry = entry.copy(description = it) },
            )

            // Save + Delete row
            val isLoading = crudState is ProductCRUDState.Loading
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                GradientButton(
                    text = if (isLoading) "Saving…" else "Save Changes",
                    disabled = !entry.isValid || isLoading,
                    onClick = {
                        if (entry.isValid && !isLoading) viewModel.updateContent(content.id, entry)
                    },
                )
                GradientButton(
                    text = if (isLoading) "Please wait…" else "🗑 Delete Content",
                    disabled = isLoading,
                    onClick = { if (!isLoading) viewModel.deleteContent(content.id) },
                )
            }

            // ── Products section ──────────────────────────────────────────────
            ProductsSection(
                contentId = content.id,
                products = contentProducts,
                isLoading = isLoadingProducts,
                viewModel = viewModel,
                onAddProduct = { navigator.goToProductItemAddPage(content.id) },
            )
        }
    }
}

/**
 * ProductsSection
 * Flat list of all products for this content, each with its full edit form always visible.
 * Products are separated by a horizontal divider. No container wraps the list.
 * Save and Delete act directly via [viewModel]; on success, the list auto-reloads.
 *
 * @param contentId Parent content ID used to reload the product list after edits.
 * @param products Current list of products for this content.
 * @param isLoading True while the initial product list is being fetched.
 * @param viewModel Shared ViewModel that owns product CRUD operations.
 * @param onAddProduct Opens the add product page in a new tab.
 */
@Composable
private fun ProductsSection(
    contentId: Long,
    products: List<ProductResponse>,
    isLoading: Boolean,
    viewModel: ProductCRUDViewModel,
    onAddProduct: () -> Unit,
) {
    val editEntries = remember { mutableStateMapOf<Long, ProductFormEntry>() }
    var savingId by remember { mutableStateOf<Long?>(null) }
    var deletingId by remember { mutableStateOf<Long?>(null) }

    val productOpState by viewModel.productOpState.collectAsState()

    // Pre-populate form entries for all loaded products
    LaunchedEffect(products) {
        products.forEach { product ->
            if (!editEntries.containsKey(product.id)) {
                editEntries[product.id] = product.toFormEntry()
            }
        }
    }

    // On success: reload the product list and reset tracking state
    LaunchedEffect(productOpState) {
        if (productOpState is ProductCRUDState.Success) {
            viewModel.loadContentById(contentId)
            savingId = null
            deletingId = null
            viewModel.resetProductState()
        }
    }

    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(0.px),
    ) {
        // Section header
        Row(
            modifier = Modifier.fillMaxWidth().padding(bottom = 16.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            SpanText(
                text = "Products (${products.size})",
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(18.px)
                    .fontWeight(FontWeight.Bold)
                    .moxTextGradient(),
            )
            GradientButton(
                text = "+ Add Product",
                buttonHeight = 40,
                onClick = onAddProduct,
            )
        }

        // Product operation error banner
        if (productOpState is ProductCRUDState.Error) {
            CRUDStatusBanner(
                message = (productOpState as ProductCRUDState.Error).message,
                isError = true,
                onDismiss = { viewModel.resetProductState() },
            )
        }

        when {
            isLoading -> Box(
                modifier = Modifier.fillMaxWidth().padding(topBottom = 24.px),
                contentAlignment = Alignment.Center,
            ) { LoadingIndicator() }

            products.isEmpty() -> SpanText(
                text = "No products yet. Click \"+ Add Product\" to get started.",
                modifier = Modifier
                    .fontFamily(FontFamily)
                    .fontSize(14.px)
                    .color(Colors.Gray),
            )

            else -> products.forEachIndexed { index, product ->
                val entry = editEntries[product.id]
                val isOpLoading = productOpState is ProductCRUDState.Loading &&
                        (savingId == product.id || deletingId == product.id)

                // Divider between products
                if (index > 0) {
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .height(1.px)
                            .backgroundColor(Colors.LightGray)
                            .margin(topBottom = 32.px),
                    )
                }

                Column(
                    modifier = Modifier.fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(20.px),
                ) {
                    // Product identity header: rank badge + brand/name + label
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(12.px),
                    ) {
                        Box(
                            modifier = Modifier
                                .size(28.px)
                                .borderRadius(14.px)
                                .styleModifier { property("background", "linear-gradient(135deg, #f47b20, #e84393)") },
                            contentAlignment = Alignment.Center,
                        ) {
                            SpanText(
                                text = "${index + 1}",
                                modifier = Modifier
                                    .fontFamily(FontFamily)
                                    .fontSize(12.px)
                                    .fontWeight(FontWeight.Bold)
                                    .color(Colors.White),
                            )
                        }

                        Column(verticalArrangement = Arrangement.spacedBy(2.px)) {
                            if (!product.brand.isNullOrBlank()) {
                                SpanText(
                                    text = product.brand,
                                    modifier = Modifier
                                        .fontFamily(FontFamily)
                                        .fontSize(11.px)
                                        .color(Colors.Gray),
                                )
                            }
                            SpanText(
                                text = product.name.orEmpty().ifBlank { "Unnamed Product" },
                                modifier = Modifier
                                    .fontFamily(FontFamily)
                                    .fontSize(15.px)
                                    .fontWeight(FontWeight.Bold)
                                    .color(DarkTextColor),
                            )
                        }

                        if (!product.label.isNullOrBlank()) {
                            SpanText(
                                text = product.label,
                                modifier = Modifier
                                    .fontFamily(FontFamily)
                                    .fontSize(11.px)
                                    .fontWeight(FontWeight.SemiBold)
                                    .color(Colors.White)
                                    .padding(leftRight = 8.px, topBottom = 3.px)
                                    .borderRadius(20.px)
                                    .styleModifier { property("background", "linear-gradient(90deg, #f47b20, #e84393)") },
                            )
                        }
                    }

                    // Full edit form — always visible
                    if (entry != null) {
                        ProductFormFields(
                            entry = entry,
                            onEntryChange = { editEntries[product.id] = it },
                        )

                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.px),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            GradientButton(
                                text = if (isOpLoading && savingId == product.id) "Saving…" else "Save Changes",
                                disabled = !entry.isValidForUpdate || productOpState is ProductCRUDState.Loading,
                                onClick = {
                                    if (entry.isValidForUpdate && productOpState !is ProductCRUDState.Loading) {
                                        savingId = product.id
                                        viewModel.updateProductItem(product.id, entry)
                                    }
                                },
                            )
                            GradientButton(
                                text = if (isOpLoading && deletingId == product.id) "Deleting…" else "🗑 Delete",
                                disabled = productOpState is ProductCRUDState.Loading,
                                onClick = {
                                    if (productOpState !is ProductCRUDState.Loading) {
                                        deletingId = product.id
                                        viewModel.deleteProductItem(product.id)
                                    }
                                },
                            )
                        }
                    }
                }
            }
        }
    }
}
